/**
 * Shift coverage hooks using TanStack Query
 * Provides data fetching for staff-to-child ratio monitoring
 */

import { useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '@clerk/clerk-react';
import { useAuthContext } from '@/contexts/AuthContext';
import { ShiftsService } from '@/api/services/ShiftsService';
import { errorStatus } from '@/lib/translateBackendError';
import type { shifts_LiveCoverageResponse } from '@/api/models/shifts_LiveCoverageResponse';
import type {
  CoverageRangeResponse,
  UnderstaffedResponse,
  GroupCoverage,
  SlotCoverage,
} from '@/types/coverage';
import { shiftKeys } from './useShifts';
import { retryUnlessClientError } from '@/lib/queryRetry';
import { endpointKey } from '@/lib/queryKeys';

// Legacy type for backward compatibility with components expecting the old response format
export type CoverageResponse = {
  date: string;
  org_id: string;
  groups: GroupCoverage[];
  sufficient_count: number;
  warning_count: number;
  critical_count: number;
};

// One key per coverage endpoint and params (#2193 B4), under ['shifts', 'coverage']
// so the shift and coverage invalidations reach every entry.
const COVERAGE_PATH = '/api/v1/shifts/coverage';

export const coverageKeys = {
  all: [...shiftKeys.all, 'coverage'] as const,
  single: (orgId: string | null | undefined, date: string) => endpointKey(orgId, COVERAGE_PATH, { date }),
  range: (orgId: string | null | undefined, from: string, to: string) =>
    endpointKey(orgId, COVERAGE_PATH, { from_date: from, to_date: to }),
  understaffed: (orgId: string | null | undefined, from: string, to: string) =>
    endpointKey(orgId, `${COVERAGE_PATH}/understaffed`, { from_date: from, to_date: to }),
  slots: (orgId: string | null | undefined, date: string) => endpointKey(orgId, `${COVERAGE_PATH}/slots`, { date }),
  live: (orgId: string | null | undefined, childcareId: string | null | undefined) =>
    endpointKey(orgId, `${COVERAGE_PATH}/live`, { childcare_id: childcareId }),
};

/** How often the live staffing figures refresh while the dashboard is open. */
export const LIVE_COVERAGE_REFRESH_MS = 60 * 1000;

/**
 * The live endpoint is not offered to this user or org (no shifts module, route
 * absent): the live surfaces hide instead of showing an error, and stop asking.
 */
export function isLiveCoverageUnavailable(error: unknown): boolean {
  const status = errorStatus(error);
  return status === 403 || status === 404;
}

/**
 * Hook to fetch coverage data for a single date.
 * Returns coverage for all groups on the specified date.
 *
 * The API now returns a unified CoverageRangeResponse format for both single dates
 * and ranges. For single-date queries, from_date equals to_date and the first day
 * entry contains group-level details. This hook transforms the response to the
 * legacy CoverageResponse format for backward compatibility with existing components.
 *
 * @param date - Date in YYYY-MM-DD format
 * @returns TanStack Query result with coverage data including group details
 */
export function useCoverage(date: string, options?: { enabled?: boolean }) {
  const { isSignedIn } = useAuth();
  const { isAuthReady, orgId } = useAuthContext();
  const select = useCallback((answer: CoverageRangeResponse) => toLegacyCoverage(answer, date), [date]);

  return useQuery({
    // The cache holds the raw answer under the day's endpoint key, which the
    // staffing week reads too (`useCoverageDays`), so one request serves both.
    queryKey: coverageKeys.single(orgId, date),
    queryFn: async () =>
      (await ShiftsService.getApiV1ShiftsCoverage(date, undefined, undefined)) as CoverageRangeResponse,
    select,
    enabled: (options?.enabled ?? true) && !!date && isSignedIn && isAuthReady,
    staleTime: 2 * 60 * 1000, // 2 minutes
    retry: retryUnlessClientError,
  });
}

/** The first day of the unified answer in the legacy single-day shape. */
export function toLegacyCoverage(rangeResponse: CoverageRangeResponse, date: string): CoverageResponse {
  const dayData = rangeResponse.days?.[0];
  return {
    date: dayData?.date ?? date,
    org_id: rangeResponse.org_id ?? '',
    groups: dayData?.groups ?? [],
    sufficient_count: dayData?.sufficient_count ?? 0,
    warning_count: dayData?.warning_count ?? 0,
    critical_count: dayData?.critical_count ?? 0,
  };
}

/**
 * Hook to fetch coverage data for a date range.
 * Returns daily coverage summaries for the specified period.
 *
 * @param from - Start date in YYYY-MM-DD format
 * @param to - End date in YYYY-MM-DD format
 * @returns TanStack Query result with range coverage data
 */
export function useCoverageRange(from: string, to: string) {
  const { isSignedIn } = useAuth();
  const { isAuthReady, orgId } = useAuthContext();

  return useQuery({
    queryKey: coverageKeys.range(orgId, from, to),
    queryFn: async () => {
      const response = await ShiftsService.getApiV1ShiftsCoverage(
        undefined,
        from,
        to
      );
      return response as CoverageRangeResponse;
    },
    enabled: !!from && !!to && isSignedIn && isAuthReady,
    staleTime: 2 * 60 * 1000,
    retry: retryUnlessClientError,
  });
}

/**
 * Hook to fetch understaffed groups for a date range.
 * Returns only groups with WARNING or CRITICAL status.
 *
 * @param from - Start date in YYYY-MM-DD format
 * @param to - End date in YYYY-MM-DD format
 * @returns TanStack Query result with understaffed groups
 */
export function useUnderstaffedGroups(from: string, to: string, options?: { enabled?: boolean }) {
  const { isSignedIn } = useAuth();
  const { isAuthReady, orgId } = useAuthContext();

  return useQuery({
    queryKey: coverageKeys.understaffed(orgId, from, to),
    queryFn: async () => {
      const response = await ShiftsService.getApiV1ShiftsCoverageUnderstaffed(
        from,
        to
      );
      return response as UnderstaffedResponse;
    },
    enabled: (options?.enabled ?? true) && !!from && !!to && isSignedIn && isAuthReady,
    staleTime: 2 * 60 * 1000,
    retry: retryUnlessClientError,
  });
}

/**
 * Hook to fetch planned coverage per 15-minute slot for one date.
 * Returns every group's slots on a shared time axis.
 *
 * @param date - Date in YYYY-MM-DD format
 * @returns TanStack Query result with slot coverage
 */
export function useSlotCoverage(date: string, options?: { enabled?: boolean }) {
  const { isSignedIn } = useAuth();
  const { isAuthReady, orgId } = useAuthContext();

  return useQuery({
    queryKey: coverageKeys.slots(orgId, date),
    queryFn: async (): Promise<SlotCoverage> =>
      ShiftsService.getApiV1ShiftsCoverageSlots(date),
    enabled: (options?.enabled ?? true) && !!date && isSignedIn && isAuthReady,
    staleTime: 2 * 60 * 1000,
    retry: retryUnlessClientError,
  });
}

/**
 * Hook to fetch the LIVE staff-to-child ratio per group for the current moment:
 * children checked in now against employees whose published shift covers now.
 * Not the planned picture of `useCoverage`; there is no date, the server always
 * answers for "now".
 *
 * Refreshes every minute and on window focus. A 403 or 404 ends both for the
 * life of the query, so a user without access does not ask again every minute.
 *
 * @param options.childcareId - Restrict the groups to one childcare
 * @param options.enabled - Caller gate (overview tab open, today selected, module access)
 */
export function useLiveCoverage(options?: { childcareId?: string | null; enabled?: boolean }) {
  const { isSignedIn } = useAuth();
  const { isAuthReady, orgId } = useAuthContext();
  const childcareId = options?.childcareId ?? undefined;

  return useQuery({
    queryKey: coverageKeys.live(orgId, childcareId),
    queryFn: (): Promise<shifts_LiveCoverageResponse> =>
      ShiftsService.getApiV1ShiftsCoverageLive(childcareId),
    enabled: (options?.enabled ?? true) && !!isSignedIn && isAuthReady && !!orgId,
    staleTime: 30 * 1000,
    refetchInterval: (query) =>
      isLiveCoverageUnavailable(query.state.error) ? false : LIVE_COVERAGE_REFRESH_MS,
    refetchOnWindowFocus: (query) => !isLiveCoverageUnavailable(query.state.error),
    retry: retryUnlessClientError,
  });
}
